import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Alert, Box, Link, Typography } from '@mui/material';
import { Mail, Lock } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';

import { UserCreateDto } from '@bandido/shared/dtos';
import { client } from '@frontend/features/common/services';
import ControlledPasswordField from '@frontend/features/common/components/ControlledPasswordField';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { theme } from '@frontend/theme';

import { AuthScreens } from '../types';

interface RegisterProps {
  onChangeScreen: (nextPage: AuthScreens) => (email?: string) => void;
}

const resolver = yupResolver(
  yup.object().shape({
    email: yup.string().required('common.form.required'),
    password: yup.string().required('common.form.required'),
    confirm: yup
      .string()
      .oneOf([yup.ref('password')], 'auth.changePassword.passwordsDontMatch')
      .required('common.form.required'),
  }),
);

export const Register: FC<RegisterProps> = ({ onChangeScreen }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ resolver });
  const [registerError, setRegisterError] = useState<string | undefined>(undefined);

  const onSubmit = async ({ email, password }: UserCreateDto) => {
    setRegisterError(undefined);
    try {
      await client.post('/auth/register', {
        email,
        password,
      });
      onChangeScreen('login')(email);
    } catch ({ response: { status } }: any) {
      setRegisterError(t(status === 409 ? 'auth.register.error' : 'common.error.server'));
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
        mt: theme.spacing(6),
      }}
    >
      <Typography variant="h6" textAlign="center">
        {t('auth.register.title')}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
          width: '100%',
        }}
      >
        <ControlledTextField
          required
          fullWidth
          control={control}
          name="email"
          label={t('auth.register.email')}
          errorMessage={t('common.form.required')}
          InputProps={{
            startAdornment: <Mail color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />

        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="password"
          label={t('auth.register.password')}
          errorMessage={t('common.form.required')}
          InputProps={{
            startAdornment: <Lock color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />

        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="confirm"
          label={t('auth.register.confirm')}
          errorMessage={errors.confirm && t(`${errors.confirm.message}`)}
          InputProps={{
            startAdornment: <Lock color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />

        <LoadingButton
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          type="submit"
          sx={{
            borderRadius: theme.spacing(1),
            marginTop: theme.spacing(2),
          }}
        >
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.register.registerMesssage')}
          </Typography>
        </LoadingButton>

        {Boolean(registerError) && <Alert severity="error">{registerError}</Alert>}
      </Box>

      <Box textAlign="center">
        <Typography
          sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
          variant="subtitle2"
        >
          {t('auth.register.logInText')}

          <Link
            color="primary"
            sx={{
              cursor: 'pointer',
              marginLeft: theme.spacing(1),
            }}
            onClick={() => onChangeScreen('login')()}
          >
            {t('auth.register.logInLink')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
